import { collection, doc, getDoc, getDocs, query, QueryConstraint } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import elasticQueryClient from '@src/repositories/ElasticQueryClient';
import type { ElasticQueryParams } from '@src/models/interfaces/general/ElasticQueryParams.interface';
import type { ElasticSearchResponse } from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import { TEventTypeSummary } from '@src/models/interfaces/eventTypeSummary.interface';
import type {
  CreateScheduleOptionParams,
  DeleteScheduleParams,
  DeleteScheduleResponse,
  SaveScheduleParams,
  SaveScheduleParamsWire,
  SaveScheduleResponse,
  TCron,
} from '@src/models/interfaces/schedule.interface';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import { ACTIONS, getRouteToScheduleOptions } from '@src/util/firebase-operations/constants';
import { createOperation } from '@src/util/firebase-operations/utils/createOperation';
import { generateFirestoreId } from '@src/util/firestore/generateFirestoreId';

class ScheduleRepository {
  private schedulesCollection: string;
  private organizationCollection: string;
  private divisionCollection: string;

  constructor() {
    this.schedulesCollection = FSCollections.SCHEDULES;
    this.organizationCollection = FSCollections.ORGANIZATIONS;
    this.divisionCollection = FSCollections.DIVISIONS;
  }

  getSchedulesPath(orgId: string, divId: string): string {
    return `${this.organizationCollection}/${orgId}/${this.divisionCollection}/${divId}/${this.schedulesCollection}`;
  }

  async list(params: ElasticQueryParams): Promise<ElasticSearchResponse | null> {
    return elasticQueryClient.query(params);
  }

  async listFirestore({
    orgId,
    divIds,
    queryConstraints = [],
  }: {
    orgId: string;
    divIds: string[];
    queryConstraints?: QueryConstraint[];
  }): Promise<Array<{ id: string } & Record<string, unknown>>> {
    const queries = divIds.map(divId => {
      const collectionRef = collection(db, this.getSchedulesPath(orgId, divId));
      return query(collectionRef, ...queryConstraints);
    });

    const results = await Promise.all(queries.map(q => getDocs(q)));

    const allDocs: Array<{ id: string } & Record<string, unknown>> = [];
    results.forEach(querySnapshot => {
      querySnapshot.docs.forEach(docSnapshot => {
        allDocs.push({
          id: docSnapshot.id,
          ...docSnapshot.data(),
        });
      });
    });

    return allDocs;
  }

  async delete(params: DeleteScheduleParams): Promise<DeleteScheduleResponse> {
    const deleteFn = apiCallable<DeleteScheduleParams, DeleteScheduleResponse>(
      API_OPERATIONS.DELETE_SCHEDULE,
    );
    const response = await deleteFn(params);
    return response.data;
  }

  newScheduleId(orgId: string, divId: string): string {
    return generateFirestoreId(
      FSCollections.ORGANIZATIONS,
      orgId,
      FSCollections.DIVISIONS,
      divId,
      FSCollections.SCHEDULES,
    );
  }

  newTriggerId(): string {
    return generateFirestoreId(FSCollections.TRIGGERS);
  }

  async callSave(params: SaveScheduleParams): Promise<SaveScheduleResponse> {
    const saveFn = apiCallable<SaveScheduleParamsWire, SaveScheduleResponse>(
      API_OPERATIONS.SAVE_SCHEDULE,
    );
    const response = await saveFn(this._toWireParams(params));
    return response.data;
  }

  private _toWireParams(params: SaveScheduleParams): SaveScheduleParamsWire {
    const { schedule } = params;
    return {
      ...params,
      schedule: {
        ...schedule,
        schedule: {
          ...schedule.schedule,
          startAt: this._toEpochMillis(schedule.schedule.startAt),
          endAt: this._toEpochMillis(schedule.schedule.endAt),
        },
      },
    };
  }

  /**
   * Firestore `Timestamp` objects do not survive the callable JSON boundary, so we
   * send the schedule window as epoch millis. Tolerant of a Timestamp, a serialized
   * Timestamp ({seconds}/{_seconds}) or a raw millis number.
   */
  private _toEpochMillis(value: TCron['startAt']): number {
    const candidate = value as unknown as {
      toMillis?: () => number;
      seconds?: number;
      _seconds?: number;
      nanoseconds?: number;
      _nanoseconds?: number;
    };

    if (typeof candidate?.toMillis === 'function') {
      return candidate.toMillis();
    }

    const seconds = candidate?.seconds ?? candidate?._seconds;
    if (typeof seconds === 'number') {
      const nanos = candidate?.nanoseconds ?? candidate?._nanoseconds ?? 0;
      return seconds * 1000 + Math.floor(nanos / 1e6);
    }

    if (typeof value === 'number') {
      return value;
    }

    throw new Error('Invalid schedule timestamp: expected Timestamp or epoch millis');
  }

  async getEventTypeById(
    orgId: string,
    divId: string,
    eventTypeId: string,
  ): Promise<(TEventTypeSummary & Record<string, unknown>) | null> {
    const eventTypeRef = doc(
      db,
      FSCollections.ORGANIZATIONS,
      orgId,
      FSCollections.DIVISIONS,
      divId,
      FSCollections.EVENT_TYPES,
      eventTypeId,
    );
    const snapshot = await getDoc(eventTypeRef);
    if (!snapshot.exists()) {
      return null;
    }
    return snapshot.data() as TEventTypeSummary & Record<string, unknown>;
  }

  async createScheduleOption({
    organizationId,
    divisionId,
    scheduleId,
    action,
  }: CreateScheduleOptionParams): Promise<void> {
    await createOperation(
      getRouteToScheduleOptions(organizationId, divisionId, scheduleId),
      null,
      { action },
      ACTIONS.CREATE,
    );
  }
}

export default ScheduleRepository;
